import { z } from "zod";

/**
 * hexColorSchema validates a 3- or 6-digit hex color string (`#abc` or `#aabbcc`),
 * matching the OpenAPI color pattern the API persists, so a client-supplied color
 * is rejected at the bus boundary before a persist sees it. It lives here beside
 * the palette it pairs with — the lively colors are all 6-digit hexes that satisfy
 * it — and is the single source of truth for the rule, imported by the tool layer
 * rather than re-declared there.
 */
export const hexColorSchema = z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);

/**
 * LIVELY_PALETTE is the set of vivid segment colors a new segment is auto-assigned
 * from. Each is a Mantine 6-shade hex (the same scale the theme draws controls in)
 * so a fresh segment reads as on-brand and saturated against the base map. The set
 * spans the hue wheel — red, pink, grape, violet, indigo, blue, cyan, teal, green,
 * lime, yellow, orange — so consecutive picks are easy to tell apart. They stay raw
 * hex literals (not CSS variables): a chosen color is persisted and painted on the map
 * by MapLibre, where CSS custom properties do not resolve.
 */
export const LIVELY_PALETTE = [
  "#fa5252",
  "#e64980",
  "#be4bdb",
  "#7950f2",
  "#4c6ef5",
  "#228be6",
  "#15aabf",
  "#12b886",
  "#40c057",
  "#82c91e",
  "#fab005",
  "#fd7e14",
] as const;

/**
 * DEFAULT_SEGMENT_COLOR is the neutral color a segment is drawn in when it carries
 * none of its own (color null = the activity's default, which the sidenav does not
 * resolve). It sits beside the palette rather than at either call site because both
 * begin-edit assemblers — the persisted chain's and the import draft's — must open an
 * edit in the color the row is already showing, and two literals is one drift away
 * from an edit that changes color the moment it opens.
 *
 * It stays a raw hex literal (Mantine gray-6), not a CSS variable, for the same reason
 * the palette does: it feeds the map edit render, where MapLibre paint expressions do
 * not resolve CSS custom properties.
 */
export const DEFAULT_SEGMENT_COLOR = "#868e96";

/**
 * pickLivelyColor returns a random vivid color for a new segment, never equal to
 * the previous segment's color so an appended segment is visually distinct from
 * the one it continues. The comparison is case-insensitive so a stored color set
 * by the picker (which may upper- or lower-case the hex) still excludes its own
 * swatch. With a palette far larger than two, a uniform pick over the remaining
 * colors stays well-distributed.
 */
export const pickLivelyColor = (previousColor?: string | null): string => {
  const previous = previousColor?.toLowerCase();
  const candidates = LIVELY_PALETTE.filter((color) => color.toLowerCase() !== previous);
  const pool: readonly string[] = candidates.length > 0 ? candidates : LIVELY_PALETTE;

  // The pool is always non-empty (the palette has many colors); the fallback to
  // the first palette color keeps the return a definite string under the
  // index-access checks.
  return pool[Math.floor(Math.random() * pool.length)] ?? LIVELY_PALETTE[0];
};

/**
 * segmentColorAt returns the palette color for a segment at a given 0-based
 * position, wrapping the palette once the position exceeds its length. It is the
 * deterministic counterpart to pickLivelyColor: where a human appending a segment
 * one at a time wants a color distinct from its predecessor (a random draw), a
 * batch projection that materialises a whole ordered list at once — an imported
 * draft — wants each position to map to a stable swatch, so the same source
 * always colors the same way and the positional `Segment #N` labels read against
 * an even spread of hues. The palette spans the wheel, so adjacent positions stay
 * easy to tell apart.
 */
export const segmentColorAt = (index: number): string =>
  LIVELY_PALETTE[index % LIVELY_PALETTE.length] ?? LIVELY_PALETTE[0];

/**
 * assignDistinctColors returns `count` LIVELY_PALETTE colors for a batch of items
 * shown side by side (the fresh suggestion candidates), each distinct from the
 * others and from every `reserved` color, spread as widely across the hue wheel as
 * the remaining palette allows. It is deterministic — no random draw — so a
 * regenerate that keeps some candidates and refills the rest assigns the fresh
 * colors stably (the same reserved set always yields the same fresh colors), which
 * keeps the result testable and the rows from reshuffling color on every batch.
 *
 * The palette is already ordered around the wheel, so "maximally spread" reduces to
 * picking evenly-strided entries from the colors still available (the palette minus
 * the reserved ones, in palette order): an even stride over a wheel-ordered list
 * maximizes the angular gap between consecutive picks. When more colors are asked
 * for than remain available, the available colors are reused in order (the wheel
 * wraps) so the function is total — it always returns exactly `count` entries —
 * rather than running short. A non-positive `count` returns an empty list.
 */
export const assignDistinctColors = (count: number, reserved: readonly string[]): string[] => {
  if (count <= 0) {
    return [];
  }

  const taken = new Set<string>(reserved.map((color) => color.toLowerCase()));
  const available = LIVELY_PALETTE.filter((color) => !taken.has(color.toLowerCase()));

  // Every reserved color covered the whole palette: fall back to the full wheel so
  // the batch still gets distinct, spread colors rather than nothing to pick from.
  const pool: readonly string[] = available.length > 0 ? available : LIVELY_PALETTE;

  // An even stride over the wheel-ordered pool maximizes the gap between consecutive
  // picks. Round the stride down to at least one so a pool smaller than `count`
  // walks one-by-one (then wraps), keeping the picks as separated as the pool allows.
  const stride = Math.max(1, Math.floor(pool.length / count));

  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    // Wrap the strided index so a `count` larger than the pool reuses colors from the
    // top of the wheel; the fallback keeps the return a definite string under the
    // index-access checks.
    colors.push(pool[(i * stride) % pool.length] ?? LIVELY_PALETTE[0]);
  }

  return colors;
};
